import { AfterViewInit, Component, OnDestroy, OnInit, signal, TemplateRef, ViewChild, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { ActivatedRoute, Params, Router, RouterOutlet } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { UpdatePageTitle } from '../../../../../store/app/app.actions';
import { TableColumn } from '@swimlane/ngx-datatable';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DatePipe } from '@angular/common';
import { Employee, EmployeeIdentityDocumentStatus } from '../../../../../_interfaces/employee.interface';
import { HttpParams } from '@angular/common/http';
import { PaginatedResults } from '@bo/ng-api';
import { EmployeeApiService } from '../../../../../_services/employee-service/api/employee-api.service';
import { ModalComponent } from '../../../../../shared/components/modals/modal/modal.component';
import { AppSelectors } from '../../../../../store/app/app.selectors';
import { AvatarComponent } from '../../../../../shared/components/avatar/avatar.component';
import { EmployeeService } from '../../../../../_services/employee-service/employee.service';
import { AppTableComponent } from '../../../../../shared/components/table/app-table.component';
import { updateParamsObject } from '../../../../../shared/utils/filter-utils';
import { employeeTableColumns } from '../../../../../_services/employee-service/employee-utils';
import { EmployeeSearchOptions } from '../../../../../shared/components/forms/search-field/search-utils';
import { OffcanvasService } from '../../../../../_services/offcanvas-service/offcanvas.service';
import {
  StatusBadgeComponent,
  statusBadgeDepartments,
} from '../../../../../shared/components/status-badge/status-badge.component';
import { TableHeaderTemplateComponent } from '../../../../../shared/components/table-header-template/table-header-template.component';
import { EmployeesFilterComponent } from '../../employees-filter/employees-filter.component';

@Component({
  selector: 'app-employee',
  imports: [
    RouterOutlet,
    DatePipe,
    NgbTooltip,
    AvatarComponent,
    AppTableComponent,
    StatusBadgeComponent,
    TableHeaderTemplateComponent,
    EmployeesFilterComponent,
  ],
  templateUrl: './employees-list.component.html',
})
export class EmployeesListComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly store = inject(Store);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly employeeApiService = inject(EmployeeApiService);
  readonly employeeService = inject(EmployeeService);
  readonly modalService = inject(NgbModal);
  readonly offcanvasService = inject(OffcanvasService);
  readonly userProfile$ = this.store.select(AppSelectors.userDetails);
  readonly subscriptions = new Subscription();
  columns: Array<TableColumn> = [];
  params: Params | undefined;

  userProfileEmail!: string;
  queryParams = new HttpParams();
  protected readonly EmployeeSearchOptions = EmployeeSearchOptions;
  private readonly columnsInitialized = signal(false);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // TODO: TemplateRef<any> can be fixed by refactoring the @bo/ng-datatable library.
  @ViewChild('employeeNameCell') employeeNameCell!: TemplateRef<any>;
  @ViewChild('dateCreatedCell') dateCreatedCell!: TemplateRef<any>;
  @ViewChild('kycStatusCell') kycStatusCell!: TemplateRef<any>;
  @ViewChild('actionsCell') actionsCell!: TemplateRef<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Employees'));
    this.offcanvasService.closeAfterNavigationEnd();
  }

  ngOnInit() {
    // Initialise columns early to prevent expression changed error
    this.columns = employeeTableColumns;

    this.subscriptions.add(
      this.userProfile$.subscribe((profile) => {
        if (profile) {
          this.userProfileEmail = profile.email;
        }
      }),
    );
  }

  ngAfterViewInit(): void {
    this.setupColumns();
  }

  private setupColumns(): void {
    if (this.columnsInitialized()) return;

    // Set templates on columns
    for (const column of this.columns) {
      if (column.prop === 'first_name') column.cellTemplate = this.employeeNameCell;
      if (column.prop === 'created') column.cellTemplate = this.dateCreatedCell;
      if (column.prop === 'idnum_status') column.cellTemplate = this.kycStatusCell;
      if (column.prop === 'actions') column.cellTemplate = this.actionsCell;
    }

    // Reassign array to trigger change detection in the table
    this.columns = [...this.columns];
    this.columnsInitialized.set(true);
  }

  onAddEmployee() {
    this.employeeService.onAddEmployee();
  }

  onViewEmployee(id: number) {
    this.offcanvasService.offCanvasNavigate(`/employees/view/${id}`);
  }

  getData(queryParams: HttpParams): Observable<PaginatedResults<Array<Employee>>> {
    return this.employeeApiService.getEmployees(queryParams);
  }

  update(params: HttpParams) {
    this.params = updateParamsObject(params);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: updateParamsObject(params),
    });
  }

  onRemoveEmployee(row: Employee) {
    if (row.email === this.userProfileEmail) {
      // You cant remove yourself
      const modalRef = this.modalService.open(ModalComponent, {
        centered: true,
        size: 'md',
      });
      modalRef.componentInstance.title = 'Whoops...';
      modalRef.componentInstance.bodyText = 'You cannot remove yourself';
      modalRef.componentInstance.buttonTextAction = 'Close';
    } else {
      this.employeeService.onRemoveEmployee(row);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected readonly EmployeeIdentityDocumentStatus = EmployeeIdentityDocumentStatus;
  protected readonly statusBadgeDepartments = statusBadgeDepartments;
}
